import { RA_MASTER_KINDS, type RaLibraryIssue } from '@felix/domain';
import { Button, FileButton, Modal } from '@mantine/core';
import { IconFileUpload } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { errorCode } from '../../personnel/source.js';
import purchasing from '../../purchasing/Purchasing.module.css';
import classes from './RaMaster.module.css';
import type { RaImportPlan, RaImportResult, RaMasterSource } from './types.js';

/**
 * Kütüphane Excel'inin içe aktarılması (K2; ADR 0086 §5; README §3.3): dosya seç → önizleme
 * (yeni, güncellenen, aynı; hatalar, uyarılar, dosyada olmayanlar; 07 matris karşılaştırması) →
 * onay. Hata varken "Import" kapalıdır; sunucu da hiçbir şey yazmaz.
 */

const ISSUE_LIMIT = 50;

function IssueList({
  issues,
  tone,
}: {
  issues: readonly RaLibraryIssue[];
  tone: 'danger' | 'warning';
}) {
  const { t } = useTranslation();
  const shown = issues.slice(0, ISSUE_LIMIT);
  return (
    <ul className={classes.issues} data-tone={tone}>
      {shown.map((issue) => (
        <li key={`${issue.sheet}-${issue.row}-${issue.field}-${issue.code}`}>
          {t('riskMaster.import.issue', {
            sheet: t(`riskMaster.sheets.${issue.sheet}`),
            row: issue.row,
            field: issue.field,
            message: t(`riskMaster.issues.${issue.code}`, { defaultValue: issue.code }),
          })}
        </li>
      ))}
      {issues.length > shown.length ? (
        <li>{t('riskMaster.import.more', { count: issues.length - shown.length })}</li>
      ) : null}
    </ul>
  );
}

/** Önizleme raporu (saf; ekran testi de çizer). */
export function ImportPlanReport({ plan }: { plan: RaImportPlan }) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`riskMaster.import.${key}`, options);
  return (
    <div className={classes.importBody}>
      <table className={classes.planTable} aria-label={k('summary')}>
        <thead>
          <tr>
            <th scope="col">{k('data')}</th>
            <th scope="col" data-numeric>
              {k('add')}
            </th>
            <th scope="col" data-numeric>
              {k('update')}
            </th>
            <th scope="col" data-numeric>
              {k('same')}
            </th>
          </tr>
        </thead>
        <tbody>
          {RA_MASTER_KINDS.map((kind) => (
            <tr key={kind}>
              <th scope="row">
                {plan.format === 'final' ? k(`final.kinds.${kind}`) : t(`riskMaster.tabs.${kind}`)}
              </th>
              <td data-numeric>{plan.counts[kind].add}</td>
              <td data-numeric>{plan.counts[kind].update}</td>
              <td data-numeric>{plan.counts[kind].same}</td>
            </tr>
          ))}
          {plan.final ? (
            <tr>
              <th scope="row">{k('final.queue')}</th>
              <td data-numeric>{plan.final.queue.add}</td>
              <td data-numeric>{plan.final.queue.update}</td>
              <td data-numeric>{plan.final.queue.same}</td>
            </tr>
          ) : null}
        </tbody>
      </table>
      {plan.final ? (
        <p className={classes.note}>
          {k('final.summary', {
            nodes: plan.final.nodes,
            review: plan.final.applicabilityReview,
          })}
          {plan.final.reopened.length > 0
            ? ` ${k('final.reopened', { count: plan.final.reopened.length, codes: plan.final.reopened.slice(0, 10).join(', ') })}`
            : ''}
        </p>
      ) : null}
      {plan.matrix ? (
        <p className={classes.note}>
          {k(`matrix.${plan.matrix.status}`, { name: plan.matrix.profileName ?? '' })}
        </p>
      ) : null}
      {plan.errors.length > 0 ? (
        <>
          <strong role="alert">{k('errors', { count: plan.errors.length })}</strong>
          <IssueList issues={plan.errors} tone="danger" />
        </>
      ) : null}
      {plan.warnings.length > 0 ? (
        <>
          <strong>{k('warnings', { count: plan.warnings.length })}</strong>
          <IssueList issues={plan.warnings} tone="warning" />
        </>
      ) : null}
      {plan.missing.length > 0 ? (
        <p className={classes.hint}>
          {k('missing', { count: plan.missing.length })}{' '}
          {plan.missing
            .slice(0, 20)
            .map((m) => m.code)
            .join(', ')}
          {plan.missing.length > 20 ? ` ${k('more', { count: plan.missing.length - 20 })}` : ''}
        </p>
      ) : null}
      <p className={classes.hint}>{plan.format === 'final' ? k('final.note') : k('statusNote')}</p>
    </div>
  );
}

export function ImportDialog({
  editor,
  onClose,
  onDone,
}: {
  editor: NonNullable<RaMasterSource['editor']>;
  onClose: () => void;
  onDone: (result: RaImportResult) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`riskMaster.import.${key}`, options);
  const [file, setFile] = useState<File | null>(null);
  const [plan, setPlan] = useState<RaImportPlan | null>(null);
  const [failure, setFailure] = useState<{ code: string; detail: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (preview: boolean) => {
    if (!file) return;
    setBusy(true);
    setFailure(null);
    try {
      const result = await editor.importFile(file, preview);
      setPlan(result.plan);
      if (!preview) onDone(result);
    } catch (error) {
      // Hatalı dosya 422 ile planı taşır (sunucu hiçbir şey yazmadı)
      const data = (error as { payload?: { plan?: RaImportPlan } } | null)?.payload;
      if (data?.plan) setPlan(data.plan);
      const detail = (error as { detail?: unknown } | null)?.detail;
      setFailure({ code: errorCode(error), detail: typeof detail === 'string' ? detail : '' });
    } finally {
      setBusy(false);
    }
  };
  const total = plan
    ? RA_MASTER_KINDS.reduce((n, kind) => n + plan.counts[kind].add + plan.counts[kind].update, 0) +
      (plan.final ? plan.final.queue.add + plan.final.queue.update : 0)
    : 0;

  return (
    <Modal
      opened
      onClose={onClose}
      title={k('title')}
      size="lg"
      closeButtonProps={{ 'aria-label': k('cancel') }}
    >
      <div className={classes.importBody}>
        <p className={classes.hint}>{k('help')}</p>
        <div>
          <FileButton
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(chosen) => {
              setFile(chosen);
              setPlan(null);
              setFailure(null);
            }}
          >
            {(props) => (
              <Button {...props} variant="default" leftSection={<IconFileUpload size={16} />}>
                {k('file')}
              </Button>
            )}
          </FileButton>
          {file ? <p className={classes.hint}>{k('fileName', { name: file.name })}</p> : null}
        </div>
        {plan ? <ImportPlanReport plan={plan} /> : null}
        {failure ? (
          <p className={purchasing.error} role="alert">
            {t(`riskMaster.errors.${failure.code}`, {
              detail: failure.detail,
              defaultValue: t('riskMaster.errors.GENERIC'),
            })}
          </p>
        ) : null}
        <div className={purchasing.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {k('cancel')}
          </Button>
          <Button variant="light" disabled={!file} loading={busy} onClick={() => void run(true)}>
            {k('preview')}
          </Button>
          <Button
            disabled={!plan || plan.errors.length > 0 || total === 0}
            loading={busy}
            onClick={() => void run(false)}
          >
            {k('apply')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
